import { z } from 'zod';

import {
  asText,
  parseTemplate,
  renderTemplate,
  TEMPLATE_ROOTS,
  type IContextPath,
  type TTemplateContext,
  type TTemplateRoot,
} from './flow-template';

export const PROMPT_ROLES = ['system', 'user', 'assistant'] as const;
export type TPromptRole = (typeof PROMPT_ROLES)[number];

export interface IPromptMessage {
  role: TPromptRole;
  content: string;
}

export const PROMPT_LIMITS = {
  maxMessages: 50,
  maxContentChars: 20_000,
} as const;

export const PROMPT_VARIABLE_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;

/** A Library prompt knows no flow (§16): only the event, the trigger and its own variables. */
export const SHARED_PROMPT_ROOTS: readonly TTemplateRoot[] = ['event', 'trigger', 'vars'];
const ANY_ROOT: readonly TTemplateRoot[] = [...TEMPLATE_ROOTS, 'vars'];

export interface IPromptProblem {
  path: (string | number)[];
  message: string;
}

/** §8.2: one optional system message first, user/assistant turns after it, a user turn last. */
export function promptOrderProblems(
  messages: readonly { role?: unknown; content?: unknown }[],
): IPromptProblem[] {
  if (messages.length === 0) return [{ path: [], message: 'add a message for the model' }];
  const problems: IPromptProblem[] = [];
  messages.forEach((message, index) => {
    if (message.role === 'system' && index > 0) {
      problems.push({
        path: [index, 'role'],
        message: 'only the first message can be the system one',
      });
    }
    if (typeof message.content !== 'string' || message.content.trim() === '') {
      problems.push({ path: [index, 'content'], message: 'write this message' });
    }
  });
  if (!messages.some((message) => message.role === 'user')) {
    problems.push({ path: [], message: 'add a user message — it is what the model answers' });
  } else if (messages[messages.length - 1].role !== 'user') {
    problems.push({
      path: [messages.length - 1, 'role'],
      message: 'the last message must be from the user',
    });
  }
  return problems;
}

export const PromptMessageSchema = z.object({
  role: z.enum(PROMPT_ROLES),
  content: z.string().max(PROMPT_LIMITS.maxContentChars),
});

export const PromptMessagesSchema = z
  .array(PromptMessageSchema)
  .max(PROMPT_LIMITS.maxMessages)
  .superRefine((messages, ctx) => {
    for (const problem of promptOrderProblems(messages)) {
      ctx.addIssue({ code: 'custom', path: problem.path, message: problem.message });
    }
  });

function refsOf(content: string, roots: readonly TTemplateRoot[]): IContextPath[] {
  return parseTemplate(content, roots).parts.flatMap((part) =>
    part.kind === 'ref' ? [part.ref] : [],
  );
}

function variableName(ref: IContextPath): string | null {
  const name = ref.path.split('.')[1];
  return name && PROMPT_VARIABLE_PATTERN.test(name) ? name : null;
}

export function sharedPromptProblems(messages: readonly IPromptMessage[]): IPromptProblem[] {
  const problems = promptOrderProblems(messages);
  messages.forEach((message, index) => {
    const at = [index, 'content'];
    let refs: IContextPath[];
    try {
      refs = refsOf(message.content, ANY_ROOT);
    } catch (error) {
      problems.push({ path: at, message: error instanceof Error ? error.message : String(error) });
      return;
    }
    for (const ref of refs) {
      const last = ref.path.split('.').at(-1) ?? 'value';
      if (ref.root === 'nodes') {
        problems.push({
          path: at,
          message: `A Library prompt can't read a step's output ({{${ref.path}}}) — use a variable like {{vars.${last}}} and bind it in the step`,
        });
      } else if (ref.root === 'item' || ref.root === 'loop') {
        problems.push({
          path: at,
          message: `A Library prompt can't read {{${ref.path}}}: it belongs to one flow — use a variable like {{vars.${last}}} and bind it in the step`,
        });
      } else if (ref.root === 'vars' && !variableName(ref)) {
        problems.push({
          path: at,
          message: `{{${ref.path}}} needs a variable name: letters, digits and _, not starting with a digit`,
        });
      }
    }
  });
  return problems;
}

export function promptVariables(messages: readonly IPromptMessage[]): string[] {
  const names = new Set<string>();
  for (const message of messages) {
    try {
      for (const ref of refsOf(message.content, ANY_ROOT)) {
        const name = ref.root === 'vars' ? variableName(ref) : null;
        if (name) names.add(name);
      }
    } catch {
      continue;
    }
  }
  return [...names].sort();
}

// "Save to Library": what a shared prompt may not read becomes a variable bound to that path.
export function liftToVariables(messages: readonly IPromptMessage[]): {
  messages: IPromptMessage[];
  vars: Record<string, string>;
} {
  const vars: Record<string, string> = {};
  const byPath = new Map<string, string>();
  const nameFor = (ref: IContextPath): string => {
    const known = byPath.get(ref.path);
    if (known) return known;
    const base =
      (ref.path.split('.').at(-1) ?? 'value')
        .replace(/[^A-Za-z0-9_]/g, '_')
        .replace(/^\d/, '_$&') || 'value';
    let name = base.slice(0, 60);
    for (let n = 2; vars[name] !== undefined; n += 1) name = `${base.slice(0, 58)}_${n}`;
    vars[name] = `{{${ref.path}}}`;
    byPath.set(ref.path, name);
    return name;
  };
  const lifted = messages.map((message) => {
    let parts;
    try {
      parts = parseTemplate(message.content, ANY_ROOT).parts;
    } catch {
      return { ...message };
    }
    const content = parts
      .map((part) => {
        if (part.kind === 'text') return part.text.replaceAll('{{', '\\{{');
        const { ref } = part;
        const shared = ref.root === 'event' || ref.root === 'trigger' || ref.root === 'vars';
        return shared ? `{{${ref.path}}}` : `{{vars.${nameFor(ref)}}}`;
      })
      .join('');
    return { role: message.role, content };
  });
  return { messages: lifted, vars };
}

// "Copy into this step": each `{{vars.x}}` becomes the template the step bound x to.
export function inlineVariables(
  messages: readonly IPromptMessage[],
  vars: Readonly<Record<string, string>>,
): IPromptMessage[] {
  return messages.map((message) => {
    let parts;
    try {
      parts = parseTemplate(message.content, ANY_ROOT).parts;
    } catch {
      return { ...message };
    }
    const content = parts
      .map((part) => {
        if (part.kind === 'text') return part.text.replaceAll('{{', '\\{{');
        const [root, name, ...rest] = part.ref.path.split('.');
        const bound = root === 'vars' ? vars[name] : undefined;
        if (bound === undefined) return `{{${part.ref.path}}}`;
        if (rest.length === 0) return bound;
        const single = /^\{\{\s*([^{}]+?)\s*\}\}$/.exec(bound);
        return single ? `{{${single[1]}.${rest.join('.')}}}` : bound;
      })
      .join('');
    return { role: message.role, content };
  });
}

/** Contents rendered as text: a value that is an object goes to the model as JSON. */
export function renderPromptMessages(
  messages: readonly IPromptMessage[],
  ctx: TTemplateContext,
  roots: readonly TTemplateRoot[] = TEMPLATE_ROOTS,
): IPromptMessage[] {
  return messages.map((message) => {
    const rendered = renderTemplate(message.content, ctx, roots);
    return {
      role: message.role,
      content: typeof rendered === 'string' ? rendered : asText(rendered),
    };
  });
}
